import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import DateFnsUtils from '@date-io/date-fns';
import NumberFormat from 'react-number-format';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  Button,
  Card,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography
} from '@material-ui/core';
import { DatePicker,  MuiPickersUtilsProvider } from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Breadcrumb,
  BackdropCircular,
  DesignedConfirmDialog
} from 'components';
import { genderOptions } from 'helpers/customers';
import {
  updateCustomerDataApi,
  createCustomerApi,
  getStoreCustomerGroupsApi,
  getStoreCustomerDataApi,
  getEavAttributeApi,
  getStoreConfigsApi
} from 'api';
import { parseISO } from 'date-fns';


const useStyles = makeStyles({
  dateRoot: {
    margin: 0,

    '& .MuiOutlinedInput-adornedEnd': {
      paddingRight: 0
    }
  },
  marketingSegment: {
    '&.MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"]': {
      padding: '0 6px',
    },
  },
});

interface ICustomAttribute {
  attribute_code: string;
  value: string;
}
interface IFormikUser {
  firstname?: string,
  lastname?: string,
  gender?: string,
  group_id?: string,
  email?: string,
  practiceName?: string,
  dob?: string,
  phoneNumber?: string,
  marketingSegment?: Array<ICustomAttribute> | string[]
}

interface ICustomerData {
  firstname?: string,
  lastname?: string,
  gender?: string,
  group_id?: string,
  email?: string,
  practiceName?: string,
  dob?: string,
  id?: number,
  store_id?: number,
  website_id?: number,
  custom_attributes?: Array<ICustomAttribute> | string[]
}

const CustomerForm = () => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [openConfirmUpdate, setOpenConfirmUpdate] = useState(false);
  const [values, setValues] = useState({});
  const {id} = useParams<{ id: string }>();
  const {
    customersData,
    customerGroups,
    updatingCustomerData,
    savingCustomerAccount
  } = useSelector((state: IReduxState) => state.customers);
  const {
    options,
    isEavAttributeLoading
  } = useSelector((state: IReduxState) => state.eavAttributes);
  const {storeConfigs} = useSelector((state: IReduxState) => state.store_configs);
  const customer = customersData[id] || {};
  const isCustomerLoading = id && !customer.id;
  const {
    profile = {},
    userStoreData: {storeCode}
  } = useSelector((state: IReduxState) => state.user);
  const isCustomer3 = storeCode === 'customer3';
  const isEditCustomer = !!Object.keys(customer).length;
  const loading = isCustomerLoading || isEavAttributeLoading;
  const phoneNumber = customer.custom_attributes?.find((a) => a.attribute_code === 'phone_number')?.value;
  const practiceName = customer?.custom_attributes?.find((a) => a.attribute_code === 'practice_name')?.value || '';
  const isReqDOB = storeConfigs.find((config) => config.path === 'customer/address/dob_show')?.value === 'req';
  const isReqGender = storeConfigs.find((config) => config.path === 'customer/address/gender_show')?.value === 'req';

  const customerMarketingSegments = (customer.custom_attributes?.find(
    (attr) => attr.attribute_code === 'marketing_segments'
  )?.value ?? '').split(',').map((value) => value.trim());

  const marketingSegment = options.filter((option) => {
    const optionValue = option.value;
    return Array.isArray(optionValue)
      ? optionValue.some((value) => customerMarketingSegments.includes(value))
      : customerMarketingSegments.includes(optionValue);
  });

  const isQuoteProStoreId = !!profile?.stores?.[0]?.quoteProStoreId;

  useEffect(() => {
    if (!customerGroups.length) {
      getStoreCustomerGroupsApi({dispatch});
    }

    if (id && !customersData[id]) {
      getStoreCustomerDataApi({dispatch, id});
    }

    if (!options.length) {
      getEavAttributeApi({
        dispatch,
        entityType: 1,
        attributeCode: 'marketing_segments'
      });
    }

    if (!storeConfigs.length) {
      getStoreConfigsApi({dispatch});
    }
  }, []);

  const handleSaveCustomerData = async () => {
    const {phoneNumber, marketingSegment, practiceName, ...fields}: IFormikUser = values;

    const customerData: ICustomerData = {
      ...fields
    };

    if (isQuoteProStoreId) {
      customerData.custom_attributes = [
        { attribute_code: 'phone_number', value: phoneNumber },
        { attribute_code: 'marketing_segments', value: marketingSegment.map((item) => item.value).join(',') }
      ];
    }

    if (isCustomer3) {
      customerData.custom_attributes.push({ attribute_code: 'practice_name', value: practiceName } as ICustomAttribute & string);
    }

    if (isEditCustomer) {
      customerData.id = customer.id;
      customerData.store_id = customer.store_id;
      customerData.website_id = customer.website_id;

      const isUpdated = await updateCustomerDataApi({
        dispatch,
        customerId: customer?.id,
        customerData
      });

      if (isUpdated) {
        history.push(`/customers/${customer?.id}/details`);
        setValues({});
      }
    } else {
      const isAccountCreated = await createCustomerApi({dispatch, customer: customerData});

      if (isAccountCreated) {
        history.push('/customers/list');
        setValues({});
      }
    }
  };

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={isEditCustomer ?
            [
              { name: 'Customers', path: '/customers/list'},
              { name: 'Details', path: `/customers/${customer.id}/details`},
              { name: 'Edit Account Information'}
            ]
            :
            [
              { name: 'Customers', path: '/customers/list'},
              { name: 'Create Account'}
            ]
          }
        />
      </div>
      {!loading &&
      <Formik
        initialValues={{
          firstname: customer?.firstname || '',
          middlename: customer?.middlename || '',
          lastname: customer?.lastname || '',
          suffix: customer?.suffix || '',
          gender: customer?.gender || '',
          group_id: customer?.group_id || '',
          email: customer?.email || '',
          dob: customer?.dob ? parseISO(customer?.dob) : new Date('1971-01-01'),
          practiceName,
          phoneNumber: phoneNumber || '',
          marketingSegment: marketingSegment || []
        }}
        validationSchema={yup.object({
          firstname: yup
            .string()
            .required('Field is required'),
          middlename: yup
            .string(),
          lastname: yup
            .string()
            .required('Field is required'),
          suffix: yup
            .string(),
          gender: isReqGender
            ? yup.string().required('Field is required')
            : yup.string(),
          group_id: yup
            .string()
            .required('Field is required'),
          email: yup
            .string()
            .email()
            .required('Field is required'),
          dob: isReqDOB
            ? yup.date().required('Field is required')
            : yup.date(),
          phoneNumber: !!isQuoteProStoreId
            ? yup
              .string()
              .min(11, 'Enter valid phone number')
              .max(11, 'Enter valid phone number')
              .required('Field is required')
            : null,
          practiceName: isCustomer3
            ? yup
              .string()
              .required('Field is required')
            : null,
          marketingSegment: yup
            .array()
        })}
        onSubmit={(values) => {
          setOpenConfirmUpdate(true);
          setValues(values);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue
        }) => (
          <form noValidate autoComplete="off" className="p-8" onSubmit={handleSubmit}>
            <Card className="mx-10 my-4">
              <div className="px-8 py-6">
                <Typography variant="h6" className="text-dark-gray text-18 font-medium">
                  {isEditCustomer
                    ? 'Edit Account Information'
                    : 'Create Customer Account'
                  }
                </Typography>
              </div>
              <Divider />
              <div className="px-8 py-6">
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Group <span className="text-error">*</span>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      select
                      fullWidth
                      variant="outlined"
                      label="Group"
                      name="group_id"
                      value={values.group_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.group_id && Boolean(errors?.group_id)}
                      helperText={touched?.group_id && errors?.group_id}
                    >
                      {customerGroups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.code}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    First Name <span className="text-error">*</span>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="First Name"
                      name="firstname"
                      value={values.firstname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.firstname && Boolean(errors?.firstname)}
                      helperText={touched?.firstname && errors?.firstname}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Middle Name
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Middle Name"
                      name="middlename"
                      value={values.middlename}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.middlename && Boolean(errors?.middlename)}
                      helperText={touched?.middlename && errors?.middlename}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Last Name <span className="text-error">*</span>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Last Name"
                      name="lastname"
                      value={values.lastname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.lastname && Boolean(errors?.lastname)}
                      helperText={touched?.lastname && errors?.lastname}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Name Suffix
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label="Name Suffix"
                      name="suffix"
                      value={values.suffix}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.suffix && Boolean(errors?.suffix)}
                      helperText={touched?.suffix && errors?.suffix}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Email <span className="text-error">*</span>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.email && Boolean(errors?.email)}
                      helperText={touched?.email && errors?.email}
                    />
                  </Grid>
                </Grid>
                {isCustomer3 &&
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Company/Practice Name <span className="text-error">*</span>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Company/Practice Name"
                      name="practiceName"
                      value={values.practiceName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.practiceName && Boolean(errors?.practiceName)}
                      helperText={touched?.practiceName && errors?.practiceName}
                    />
                  </Grid>
                </Grid>
                }
                {isQuoteProStoreId &&
                  <Grid container spacing={3} className="mb-5 flex items-center">
                    <Grid item xs={12} sm={3}>
                      Phone Number <span className="text-error">*</span>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <NumberFormat
                        customInput={TextField}
                        variant="outlined"
                        format="+# (###) ###-####"
                        mask="_"
                        required
                        fullWidth
                        label="Phone"
                        name="phoneNumber"
                        value={values.phoneNumber}
                        onValueChange={(value) => {
                          setFieldValue('phoneNumber', String(value.floatValue));
                        }}
                        onBlur={handleBlur}
                        error={touched?.phoneNumber && Boolean(errors?.phoneNumber)}
                        helperText={touched?.phoneNumber && errors?.phoneNumber}
                      />
                    </Grid>
                  </Grid>
                }
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Gender {isReqGender && <span className="text-error">*</span>}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      select
                      fullWidth
                      variant="outlined"
                      label="Gender"
                      name="gender"
                      required={isReqGender}
                      value={values.gender}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.gender && Boolean(errors?.gender)}
                      helperText={touched?.gender && errors?.gender}
                    >
                      {genderOptions.map((gender) => (
                        <MenuItem key={gender.value} value={gender.value}>
                          {gender.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
                <Grid container spacing={3} className="mb-5 flex items-center">
                  <Grid item xs={12} sm={3}>
                    Date of Birth {isReqDOB && <span className="text-error">*</span>}
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <DatePicker
                        className={classes.dateRoot}
                        variant="inline"
                        inputVariant="outlined"
                        format="MM/dd/yyyy"
                        margin="normal"
                        autoOk
                        disableFuture
                        required={isReqDOB}
                        fullWidth
                        label="Date of Birth"
                        value={values.dob}
                        onChange={(date) => {
                          setFieldValue('dob', date.toLocaleDateString('en-US'));
                        }}
                        onBlur={handleBlur}
                        error={touched?.dob && Boolean(errors?.dob)}
                        helperText={touched?.dob && errors?.dob}
                      />
                    </MuiPickersUtilsProvider>
                  </Grid>
                </Grid>
                {isQuoteProStoreId && !!options.length &&
                  <Grid container spacing={3} className="mb-5 flex items-center">
                    <Grid item xs={12} sm={3}>
                      Marketing Segment
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        multiple
                        fullWidth
                        disableClearable
                        size="small"
                        value={values.marketingSegment.flatMap((arr) => arr)}
                        classes={{
                          inputRoot: classes.marketingSegment
                        }}
                        options={options}
                        getOptionLabel={(option) => option.label}
                        getOptionSelected={(option, value) => option.value === value.value}
                        filterSelectedOptions
                        onChange={(e, value) => setFieldValue('marketingSegment', value)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Marketing Segment"
                            variant="outlined"
                            error={touched.marketingSegment && Boolean(errors.marketingSegment)}
                            helperText={touched.marketingSegment && errors.marketingSegment}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                }
              </div>
            </Card>
            <div className="flex justify-end items-center w-full px-10 py-5">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  isEditCustomer
                    ? history.push(`/customers/${customer.id}/details`)
                    : history.push('/customers/list');
                }}
                className="mr-4"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </div>
          </form>
        )}
      </Formik>}
      <DesignedConfirmDialog
        open={openConfirmUpdate}
        setOpen={setOpenConfirmUpdate}
        onConfirm={handleSaveCustomerData}
      >
        {isEditCustomer
          ? 'Are you sure you want to update customer information?'
          : 'Are you sure you want to create a customer account?'
        }
      </DesignedConfirmDialog>
      <BackdropCircular loading={updatingCustomerData || savingCustomerAccount || loading} />
    </div>
  );
};

export default CustomerForm;
